import { useTranslation } from 'react-i18next';
import { Typography } from 'app/components/ui';
import Pagination from 'shared/data-display/Pagination';
import { Modal as BootstrapModal } from 'react-bootstrap';
import { useMemo, useState } from 'react';
import { usePagination } from 'app/utils/hooks/usePagination';
import { OverviewTable } from './OverviewTable/OverviewTable';
import { OverviewHeader } from './OverviewHeader/OverviewHeader';
import { useTagsByDocument, useDocumentTaxonomyConfig } from 'entities/tag';
import { FilterGroup, useFilteredTags } from 'app/components/editor/sidebar/tags/TagsFilter';
import { formatTaxonomyName } from 'app/utils/tag-utils';

import './Overview.scss';
import { useEditorDocumentOptional } from 'app/api/documents/useEditorData';

export const Overview = () => {
  const { t } = useTranslation(['all', 'tag', 'taxonomy']);

  const [show, setShow] = useState(false);
  const { data: pDocument } = useEditorDocumentOptional();

  const { data: { tags, taxonomy } = { tags: [], taxonomy: {} } } = useTagsByDocument(show ? pDocument?.superId : undefined, {
    include: ['metadata', 'concept', 'taxonomy'],
  });
  const { data: taxonomyConfig } = useDocumentTaxonomyConfig(show ? pDocument?.superId : undefined);
  const totalTags = tags.length;

  const tagsWithTaxonomy = useMemo(() => tags.map((tag) => ({ ...tag, taxonomy: taxonomy[tag.taxonomyId] })), [tags, taxonomy]);

  const totalMandatoryTags = useMemo(() => Object.values(taxonomy).reduce((sum, t) => sum + (t?.totalMandatoryTags ?? 0), 0), [taxonomy]);

  const stats = useMemo(() => {
    const counts = { approved: 0, mandatory: 0, draft: 0, needsAttention: 0, invalid: 0 };
    for (const tag of tags) {
      if (tag.status === 'approved') counts.approved++;
      else if (tag.status === 'draft') counts.draft++;
      else if (tag.status === 'need_attention') counts.needsAttention++;
      if (tag.validationStatus === 'invalid') counts.invalid++;
      if (tag.concept?.isMandatory) counts.mandatory++;
    }
    return [
      { label: t('tag:total.tags'), value: totalTags, type: 'total' },
      { label: t('tag:mandatory.label'), value: counts.mandatory, type: 'mandatory' },
      { label: t('tag:status.approved'), value: counts.approved, type: 'approved' },
      { label: t('tag:status.draft'), value: counts.draft, type: 'draft' },
      { label: t('tag:status.need_attention'), value: counts.needsAttention, type: 'need_attention' },
      { label: t('tag:status.invalid'), value: counts.invalid, type: 'invalid' },
    ];
  }, [tags, totalTags, t]);

  const taxonomyFilterOptions = useMemo(() => {
    if (!taxonomyConfig?.taxonomySelections?.length) return [];
    return taxonomyConfig.taxonomySelections.map((ts) => ({
      label: ts.taxonomy?.name ? formatTaxonomyName(ts.taxonomy.name) : ts.taxonomyId,
      value: ts.taxonomy?.name || ts.taxonomyId,
    }));
  }, [taxonomyConfig?.taxonomySelections]);

  const filterGroups: FilterGroup[] = [
    {
      label: t('tag:taxonomy.plural'),
      value: 'taxonomy',
      icon: <i className="fa-solid fa-layer-group" />,
      options: taxonomyFilterOptions,
    },
    {
      label: t('tag:highlighted.option.status'),
      value: 'status',
      icon: <i className="fa-solid fa-spinner" />,
      options: [
        { label: t('tag:status.approved'), value: 'approved' },
        { label: t('tag:status.draft'), value: 'draft' },
        { label: t('tag:status.need_attention'), value: 'need_attention' },
        { label: t('tag:status.verified'), value: 'verified' },
      ],
    },
    {
      label: t('tag:validation.label'),
      value: 'validated',
      icon: <i className="fa-solid fa-flag" />,
      options: [
        { label: t('tag:status.invalid'), value: 'invalid' },
        { label: t('tag:status.unvalidated'), value: 'unvalidated' },
        { label: t('tag:status.valid'), value: 'valid' },
      ],
    },
    {
      label: t('tag:type'),
      value: 'type',
      icon: <i className="fa-solid fa-tags" />,
      options: [
        { label: t('tag:type.numeric'), value: 'nonFraction' },
        { label: t('tag:type.textBlock'), value: 'nonNumeric' },
      ],
    },
  ];

  const { filters, filteredTags, onAddFilter, onRemoveFilter, searchInput, setSearchInput } = useFilteredTags(tagsWithTaxonomy, filterGroups);

  const { paginatedItems, page, setCurrentPage, pageSize } = usePagination(filteredTags, { pageSize: 5 });

  const tagCategories = [
    t('tag:name'),
    t('tag:type'),
    t('taxonomy:title'),
    t('tag:highlighted.option.status'),
    t('tag:validation.label'),
    t('tag:mandatory.label'),
    t('all:tags.page'),
  ];

  return (
    <>
      <button className="tag-toolbar__button" onClick={() => setShow(true)}>
        <i className="fa-regular fa-lg fa-eye" />
        <Typography variant="body-5" fontWeight="bold">
          {t('tag:overview.label')}
        </Typography>
      </button>

      <BootstrapModal show={show} onHide={() => setShow(false)} size="xl" contentClassName="overview-modal">
        <BootstrapModal.Header className="overview-modal__header">
          <OverviewHeader
            stats={stats}
            totalMandatoryTags={totalMandatoryTags}
            totalTags={totalTags}
            searchInput={searchInput}
            setSearchInput={setSearchInput}
            onClose={() => setShow(false)}
            filters={filters}
            onAddFilter={onAddFilter}
            onRemoveFilter={onRemoveFilter}
            filterGroups={filterGroups}
          />
        </BootstrapModal.Header>

        <BootstrapModal.Body className="overview-modal__body">
          <div className="overview-modal__table-wrapper">
            <OverviewTable categories={tagCategories} tableData={paginatedItems} onNavigate={() => setShow(false)} />
          </div>
        </BootstrapModal.Body>

        <div className="overview-modal__footer">
          <Pagination onPaginate={(p: number) => setCurrentPage(p)} page={page} pageSize={pageSize} />
        </div>
      </BootstrapModal>
    </>
  );
};
